import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { findOrCreateLinkedThread } from '../services/messageService';
import toast from 'react-hot-toast';

/**
 * "Discuss this" — open the conversation attached to a record, creating it on
 * first use.
 *
 * The find-or-create is the whole point. If this simply started a new thread
 * every time, a single disputed expense would end up spread across five
 * conversations and the link would make the record harder to follow rather than
 * easier. `findOrCreateLinkedThread` keys on `linkedKey` so both parents, from
 * either platform, land in the same thread.
 *
 * Child tags are seeded from the record: an expense filed against one child
 * produces a conversation already tagged to that child, so it shows up under
 * that child's filter without anyone doing extra work.
 *
 * @returns {{openDiscussion: function, opening: boolean}}
 */
export function useDiscussThread() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { familyId, family } = useFamily();
  const [opening, setOpening] = useState(false);

  /**
   * @param {Object} entity
   * @param {'expense'|'activity'|'swap'} entity.type
   * @param {string} entity.id
   * @param {string} entity.label - denormalized for display in the thread header
   * @param {string[]} [entity.childIds] - seed tags for the conversation
   */
  const openDiscussion = useCallback(async (entity) => {
    if (!familyId || !currentUser || !entity?.id) return;
    setOpening(true);
    try {
      const thread = await findOrCreateLinkedThread(familyId, {
        linkedEntity: { type: entity.type, id: entity.id, label: entity.label || '' },
        createdBy: currentUser.uid,
        participantIds: family?.parentIds || [],
        childIds: entity.childIds || [],
      });
      navigate(`/messages?thread=${thread.id}`);
    } catch (error) {
      console.error('Could not open discussion:', error);
      toast.error('Could not open the conversation');
    } finally {
      setOpening(false);
    }
  }, [familyId, currentUser, family, navigate]);

  return { openDiscussion, opening };
}

export default useDiscussThread;
